import React from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useStore } from "@/context/StoreContext";
import { img } from "@/lib/img";
import { X, Minus, Plus, Truck } from "lucide-react";

export const CartDrawer = () => {
  const {
    cart, cartOpen, setCartOpen, updateQty, removeFromCart,
    subtotal, savings, freeShipRemaining, freeShipPct,
  } = useStore();

  const inr = (n) => `₹${n.toLocaleString("en-IN")}`;

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-lemce-ivory border-l border-lemce-beige p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetTitle className="sr-only">Your Shopping Bag</SheetTitle>
        <SheetDescription className="sr-only">Review the items in your bag and proceed to checkout.</SheetDescription>
        <div className="flex items-center justify-between px-6 py-5 border-b border-lemce-beige">
          <h2 className="font-serif text-xl">Your Bag ({cart.length})</h2>
        </div>

        <div className="px-6 py-4 border-b border-lemce-beige">
          {freeShipRemaining > 0 ? (
            <p className="text-xs tracking-wide text-lemce-charcoal flex items-center gap-2">
              <Truck className="w-4 h-4" strokeWidth={1.5} />
              You're <span className="font-semibold">{inr(freeShipRemaining)}</span> away from FREE SHIPPING.
            </p>
          ) : (
            <p className="text-xs tracking-wide text-lemce-matte flex items-center gap-2 font-medium">
              <Truck className="w-4 h-4" strokeWidth={1.5} /> You've unlocked complimentary shipping.
            </p>
          )}
          <div className="mt-3 h-1 w-full bg-lemce-beige overflow-hidden">
            <div className="h-full bg-lemce-matte transition-all duration-500" style={{ width: `${freeShipPct}%` }} />
          </div>
        </div>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
            <p className="text-lemce-grey text-sm">Your bag is empty.</p>
            <button
              onClick={() => setCartOpen(false)}
              className="text-xs tracking-widest uppercase border-b border-lemce-matte pb-1"
            >
              Continue Shopping
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {cart.map((item) => (
                <div key={item.key} className="flex gap-4" data-testid={`cart-item-${item.slug}`}>
                  <Link to={`/product/${item.slug}`} onClick={() => setCartOpen(false)} className="w-20 h-24 bg-lemce-beige shrink-0 overflow-hidden">
                    <img src={img(item.image, 240)} alt={item.name} loading="lazy" className="w-full h-full object-contain" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <h3 className="text-sm font-medium leading-snug">{item.name}</h3>
                      <button onClick={() => removeFromCart(item.key)} aria-label="Remove" data-testid={`cart-remove-${item.slug}`}>
                        <X className="w-4 h-4 text-lemce-grey hover:text-lemce-matte" strokeWidth={1.5} />
                      </button>
                    </div>
                    <p className="text-xs text-lemce-grey mt-0.5">{item.color} · {item.size}{item.pack_size > 1 ? ` · Pack of ${item.pack_size}` : ""}</p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-lemce-beige">
                        <button onClick={() => updateQty(item.key, item.qty - 1)} aria-label="Decrease" className="w-8 h-8 flex items-center justify-center hover:bg-lemce-beige">
                          <Minus className="w-3 h-3" strokeWidth={1.5} />
                        </button>
                        <span className="w-8 text-center text-sm">{item.qty}</span>
                        <button onClick={() => updateQty(item.key, item.qty + 1)} aria-label="Increase" className="w-8 h-8 flex items-center justify-center hover:bg-lemce-beige">
                          <Plus className="w-3 h-3" strokeWidth={1.5} />
                        </button>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">{inr(item.price * item.qty)}</p>
                        {item.mrp > item.price && <p className="text-xs text-lemce-grey line-through">{inr(item.mrp * item.qty)}</p>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-lemce-beige px-6 py-5 space-y-4">
              {savings > 0 && (
                <div className="flex justify-between text-xs text-lemce-taupe font-medium">
                  <span>You're saving</span><span>{inr(savings)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-lemce-charcoal">Subtotal</span>
                <span className="font-semibold" data-testid="cart-subtotal">{inr(subtotal)}</span>
              </div>
              <Link
                to="/checkout"
                onClick={() => setCartOpen(false)}
                data-testid="secure-checkout-btn"
                className="block w-full text-center bg-lemce-matte text-lemce-ivory py-4 text-xs tracking-widest uppercase hover:bg-lemce-charcoal transition-colors"
              >
                Secure Checkout
              </Link>
              <button onClick={() => setCartOpen(false)} className="w-full text-center text-xs tracking-widest uppercase text-lemce-grey hover:text-lemce-matte">
                Continue Shopping
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
